import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import CartSummary from '../components/CartSummary';
import RestaurantInfoModal from '../components/RestaurantInfoModal';
import {useMerchant} from '../context/MerchantContext';
import {BannerDto} from '../types';
import BannerCarousel from '../components/banners/BannerCarousel';
import BannerDetailModal from '../components/BannerDetailModal';
import {collectVisiblePromotions, getPromotionSummary, getPromotionTypeLabel} from '../utils/promotionUtils';

// Hooks
import {useRestaurantStatus} from '../hooks/useRestaurantStatus';
import {useMenuFetch} from '../hooks/useMenuFetch';
import {useScrollSpy} from '../hooks/useScrollSpy';

// Components
import {CashbackCard, RestaurantCover, RestaurantInfo} from '../components/menu/RestaurantHeader';
import MenuFilters from '../components/menu/MenuFilters';
import CategoryList from '../components/menu/CategoryList';

const SkeletonBlock = styled.div<{ width?: string; height?: string; $themeColor: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1rem'};
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #edf2f7 20%,
    ${props => `${props.$themeColor}1A`} 38%,
    #edf2f7 62%
  );
  background-size: 400% 100%;
  animation: menu-skeleton-loading 1.4s ease infinite;

  @keyframes menu-skeleton-loading {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

const MenuItemSkeletonCard = styled.div`
  min-height: clamp(120px, 18vw, 160px);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 .125rem .25rem rgba(0, 0, 0, .075);
  overflow: hidden;
`;

const HeroSkeleton = styled.div`
  height: clamp(190px, 32vw, 320px);
  border-radius: 0 0 18px 18px;
  overflow: visible;
  position: relative;
  background: #edf2f7;

  @media (min-width: 768px) {
    border-radius: 0 0 24px 24px;
  }
`;

const FloatingLogoSkeleton = styled.div<{ $themeColor: string }>`
  position: absolute;
  left: 50%;
  bottom: -44px;
  transform: translateX(-50%);
  width: 96px;
  height: 96px;
  border: 4px solid #fff;
  border-radius: 18px;
  background: linear-gradient(
    90deg,
    #edf2f7 20%,
    ${props => `${props.$themeColor}1A`} 38%,
    #edf2f7 62%
  );
  background-size: 400% 100%;
  animation: menu-skeleton-loading 1.4s ease infinite;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
`;

const InfoSkeletonCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1.25rem;
  margin-top: 3.25rem;
  box-shadow: 0 .125rem .25rem rgba(0, 0, 0, .075);
`;

const PromotionsSection = styled.section`
  margin: 1rem 0 1.5rem;
`;

const PromotionsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const PromotionsSectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.05rem, 2.3vw, 1.4rem);
  font-weight: 800;
  color: #111827;
`;

const PromotionsSectionSubtitle = styled.p`
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.92rem;
`;

const PromotionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
`;

const PromotionCard = styled.article<{ $themeColor: string }>`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 1rem;
  background:
    linear-gradient(135deg, ${props => `${props.$themeColor}14`} 0%, rgba(255, 255, 255, 0.98) 56%),
    #ffffff;
  border: 1px solid ${props => `${props.$themeColor}26`};
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  min-height: 100%;
`;

const PromotionTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const PromotionName = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #111827;
`;

const PromotionChip = styled.span<{ $themeColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: ${props => `${props.$themeColor}18`};
  color: ${props => props.$themeColor};
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
`;

const PromotionDescription = styled.p`
  margin: 0 0 0.85rem;
  color: #374151;
  font-size: 0.92rem;
  line-height: 1.45;
`;

const PromotionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
  color: #4b5563;
  font-size: 0.82rem;
`;

const PromotionMetaLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
`;

const PromotionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PromotionCategoryTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const PromotionCategoryTag = styled.span<{ $themeColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: ${props => props.$themeColor};
  font-size: 0.72rem;
  font-weight: 700;
`;

const PromotionLink = styled.button<{ $themeColor: string }>`
  border: none;
  background: transparent;
  color: ${props => props.$themeColor};
  font-weight: 800;
  font-size: 0.85rem;
  padding: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FilterSkeletonBar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
  margin: 1rem 0 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 220px minmax(0, 1fr);
  }
`;

const MenuPage: React.FC = () => {
  const { restaurantId: paramId } = useParams<{ restaurantId?: string }>();
  const { 
    merchantId: contextId, 
    restaurant: merchantRestaurant, 
    isLoading: isMerchantLoading,
    error: merchantError,
  } = useMerchant();
  const restaurantId = paramId || contextId || undefined;
  
  const restaurant = merchantRestaurant;
  const themeColor = restaurant?.theme?.primary || '#e74c3c';
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBanner, setSelectedBanner] = useState<BannerDto | null>(null);
  const [isRestaurantInfoModalOpen, setIsRestaurantInfoModalOpen] = useState(false);

  // Hooks
  const { isRestaurantOpen, getNextOpeningTime, getClosingTime } = useRestaurantStatus(restaurant);
  const { 
    categories, 
    isLoading, 
    error: menuError,
  } = useMenuFetch(restaurantId, null);
  
  const {
    logoRef,
    showStickyHeaderInfo,
    isCategoryDropdownVisible,
    currentVisibleCategory,
    activeCategoryId,
    setActiveCategoryId
  } = useScrollSpy(categories, isLoading);
  const visiblePromotions = useMemo(() => collectVisiblePromotions(categories), [categories]);

  useEffect(() => {
    if (restaurant?.name) {
      document.title = restaurant.name;
    }
  }, [restaurant]);

  // Filter menu items based on search query
  const filteredCategories = categories?.map(category => ({
    ...category,
    items: category.items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  })).filter(category => category.items.length > 0);

  // Handle category change
  const handleCategoryChange = (categoryId: string) => {
    setActiveCategoryId(categoryId);

    // Scroll to the selected category
    if (categoryId) {
      const categoryElement = document.getElementById(`category-${categoryId}`);
      const rootElement = document.getElementById('root');
      if (categoryElement && rootElement) {
        const elementPosition = categoryElement.getBoundingClientRect().top + rootElement.scrollTop;
        const headerOffset = 100;
        rootElement.scrollTo({
          top: elementPosition - headerOffset,
          behavior: 'smooth'
        });
      }
    }
  };

  // Handle click on category indicator in header
  const handleCategoryIndicatorClick = () => {
    const filterContainer = document.querySelector('.filter-search-container');
    const rootElement = document.getElementById('root');
    if (filterContainer && rootElement) {
      const elementPosition = filterContainer.getBoundingClientRect().top + rootElement.scrollTop;
      const headerOffset = 100;
      rootElement.scrollTo({
        top: elementPosition - headerOffset,
        behavior: 'smooth'
      });
    }
  };

  // Handle search input change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setActiveCategoryId(null);
  };

  const renderRestaurantSkeleton = () => (
    <>
      <div className="container-md px-0 px-md-4">
        <HeroSkeleton>
          <SkeletonBlock width="100%" height="100%" $themeColor={themeColor} />
          <FloatingLogoSkeleton $themeColor={themeColor} />
        </HeroSkeleton>
      </div>
      <div className="container-md px-md-4">
        <InfoSkeletonCard>
          <SkeletonBlock width="48%" height="1.6rem" $themeColor={themeColor} />
          <div className="d-flex flex-wrap gap-2 mt-3">
            <SkeletonBlock width="96px" height="1.5rem" $themeColor={themeColor} />
            <SkeletonBlock width="120px" height="1.5rem" $themeColor={themeColor} />
            <SkeletonBlock width="86px" height="1.5rem" $themeColor={themeColor} />
          </div>
        </InfoSkeletonCard>
      </div>
    </>
  );

  const renderFiltersSkeleton = () => (
    <FilterSkeletonBar>
      <SkeletonBlock width="100%" height="42px" $themeColor={themeColor} />
      <SkeletonBlock width="100%" height="42px" $themeColor={themeColor} />
    </FilterSkeletonBar>
  );

  const renderPromotionsHighlight = () => {
    if (visiblePromotions.length === 0) return null;

    return (
      <PromotionsSection>
        <PromotionsSectionHeader>
          <div>
            <PromotionsSectionTitle>Promoções em destaque</PromotionsSectionTitle>
            <PromotionsSectionSubtitle>
              Ofertas ativas e visíveis no cardápio para facilitar a decisão de compra.
            </PromotionsSectionSubtitle>
          </div>
          <PromotionLink $themeColor={themeColor} onClick={() => navigate('/promocoes')}>
            Ver todas
          </PromotionLink>
        </PromotionsSectionHeader>

        <PromotionsGrid>
          {visiblePromotions.slice(0, 4).map(promotion => (
            <PromotionCard key={promotion.id} $themeColor={themeColor}>
              <PromotionTop>
                <div>
                  <PromotionName>{promotion.name}</PromotionName>
                  <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                    <PromotionChip $themeColor={themeColor}>
                      <i className="fas fa-bolt" />
                      {getPromotionTypeLabel(promotion.type)}
                    </PromotionChip>
                    {promotion.showInMenu && (
                      <PromotionChip $themeColor={themeColor}>
                        <i className="fas fa-eye" />
                        Exibida no menu
                      </PromotionChip>
                    )}
                  </div>
                </div>
              </PromotionTop>

              <PromotionDescription>
                {promotion.description || 'Promoção ativa para melhorar a experiência e a conversão no cardápio.'}
              </PromotionDescription>

              <PromotionMeta>
                <PromotionMetaLine>
                  <i className="fas fa-calendar-alt mt-1" style={{ color: themeColor }} />
                  <span>{getPromotionSummary(promotion)}</span>
                </PromotionMetaLine>
                <PromotionMetaLine>
                  <i className="fas fa-layer-group mt-1" style={{ color: themeColor }} />
                  <span>
                    {promotion.categories?.length > 0
                      ? promotion.categories.map(category => category.name).join(' • ')
                      : `${promotion.categoryIds?.length || 0} categoria(s) vinculada(s)`}
                  </span>
                </PromotionMetaLine>
              </PromotionMeta>

              <PromotionFooter>
                <PromotionCategoryTags>
                  {promotion.allowWithCoupon && (
                    <PromotionCategoryTag $themeColor={themeColor}>Com cupom</PromotionCategoryTag>
                  )}
                  {promotion.allowWithCashback && (
                    <PromotionCategoryTag $themeColor={themeColor}>Com cashback</PromotionCategoryTag>
                  )}
                  {!promotion.allowWithOtherPromotions && (
                    <PromotionCategoryTag $themeColor={themeColor}>Sem acúmulo</PromotionCategoryTag>
                  )}
                </PromotionCategoryTags>
                <PromotionLink $themeColor={themeColor} onClick={() => navigate('/promocoes')}>
                  Explorar oferta
                </PromotionLink>
              </PromotionFooter>
            </PromotionCard>
          ))}
        </PromotionsGrid>
      </PromotionsSection>
    );
  };

  const renderCatalogSkeleton = () => (
    <div className="main-content py-2">
      {Array.from({ length: 3 }).map((_, categoryIndex) => (
        <div key={categoryIndex} className="category-section mb-5">
          <div className="border-bottom pb-2 mb-4">
            <SkeletonBlock width="180px" height="1.25rem" $themeColor={themeColor} />
          </div>
          <div className="row g-3">
            {Array.from({ length: 4 }).map((__, itemIndex) => (
              <div key={itemIndex} className="col-12 col-md-6 col-sm-6">
                <MenuItemSkeletonCard>
                  <div className="card-body p-0 d-flex flex-row h-100">
                    <div className="p-2 p-md-3 d-flex flex-column flex-grow-1 min-w-0">
                      <div className="flex-grow-1">
                        <SkeletonBlock width="72px" height="18px" $themeColor={themeColor} />
                        <div style={{ marginTop: '0.75rem' }}>
                          <SkeletonBlock width="75%" height="1rem" $themeColor={themeColor} />
                        </div>
                        <div style={{ marginTop: '0.6rem' }}>
                          <SkeletonBlock width="96%" height="0.75rem" $themeColor={themeColor} />
                        </div>
                        <div style={{ marginTop: '0.45rem' }}>
                          <SkeletonBlock width="66%" height="0.75rem" $themeColor={themeColor} />
                        </div>
                      </div>
                      <SkeletonBlock width="82px" height="1.1rem" $themeColor={themeColor} />
                    </div>
                    <div className="m-2 rounded-3 overflow-hidden position-relative flex-shrink-0" style={{ width: 'clamp(70px, 22vw, 110px)', height: 'clamp(70px, 22vw, 110px)' }}>
                      <SkeletonBlock width="100%" height="100%" $themeColor={themeColor} />
                    </div>
                  </div>
                </MenuItemSkeletonCard>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  if ((merchantError && !restaurant) || (menuError && !isLoading && categories.length === 0)) {
    return (
      <Layout>
        <div className="d-flex flex-column justify-content-center align-items-center text-center px-3" style={{ height: '400px' }}>
          <i className="fas fa-exclamation-circle text-danger mb-4" style={{ fontSize: '4rem' }}></i>
          <h2 className="display-6 fw-bold mb-3">Ops! Algo deu errado.</h2>
          <p className="lead text-muted mb-4 mx-auto" style={{ maxWidth: '600px' }}>
            Não foi possível carregar as informações da loja. Por favor, tente novamente mais tarde.
          </p>
          <button className="btn btn-primary btn-lg px-5 shadow-sm" onClick={() => window.location.reload()}>
            Recarregar página
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      currentCategory={currentVisibleCategory || ''}
      isCategoryVisible={!isCategoryDropdownVisible && !!currentVisibleCategory}
      onCategoryClick={handleCategoryIndicatorClick}
      logoUrl={restaurant?.logo || 'https://via.placeholder.com/150x150'}
      showRestaurantInfo={showStickyHeaderInfo}
      categories={categories}
      activeCategory={activeCategoryId || ''}
      onCategoryChange={handleCategoryChange}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      noContainer={true}
    >
      {isMerchantLoading && !restaurant ? (
        renderRestaurantSkeleton()
      ) : (
        <div className="container-md px-0 px-md-4">
          <RestaurantCover 
            restaurant={restaurant}
            logoRef={logoRef}
          />
        </div>
      )}

      <div className="container-md px-md-4">
        <div className="row g-3">
          <div className="col-md-12 col-lg-8 col-xl-9">
            {restaurant && (
              <>
                <RestaurantInfo 
                  restaurant={restaurant}
                  isRestaurantOpen={isRestaurantOpen()}
                  nextOpeningTime={getNextOpeningTime()}
                  closingTime={getClosingTime()}
                  onInfoClick={() => setIsRestaurantInfoModalOpen(true)}
                />
                
                {/* Cashback Card for Mobile */}
                <div className="d-lg-none mb-3">
                  <CashbackCard restaurant={restaurant} />
                </div>
              </>
            )}

            {isMerchantLoading && !restaurant ? (
              renderFiltersSkeleton()
            ) : (
              <MenuFilters 
                categories={categories}
                activeCategory={activeCategoryId || ''}
                searchQuery={searchQuery}
                onCategoryChange={handleCategoryChange}
                onSearchChange={handleSearchChange}
                themeColor={themeColor}
              />
            )}

            {restaurant?.banners && restaurant.banners.length > 0 && (
              <div className="mb-4">
                <BannerCarousel 
                  banners={restaurant.banners} 
                  onBannerClick={setSelectedBanner} 
                />
              </div>
            )}

            {renderPromotionsHighlight()}

            {/* Content area */}
            <div className="menu-content-area">
              {isLoading && categories.length === 0 ? (
                renderCatalogSkeleton()
              ) : searchQuery && filteredCategories?.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-search text-muted mb-3 fs-1 opacity-25"></i>
                  <h3 className="fw-bold">Nenhum resultado</h3>
                  <p className="text-muted">Tente buscar por outro termo.</p>
                </div>
              ) : (
                <CategoryList categories={searchQuery ? filteredCategories : categories} />
              )}
            </div>
          </div>

          <div className="col-md-12 col-lg-4 col-xl-3 d-none d-lg-block">
            {restaurant && <CashbackCard restaurant={restaurant} />}
            <div className={`sticky-top ${restaurant?.cashbackConfig?.enabled ? 'mt-3' : ''}`} style={{ top: '6rem', zIndex: 10 }}>
              <CartSummary isRestaurantOpen={isRestaurantOpen()} />
            </div>
          </div>
        </div>
      </div>

      {/* Carrinho no mobile agora é global (renderizado no Layout) e aberto
          pela aba Sacola da BottomNav. */}

      {restaurant && (
        <RestaurantInfoModal
          restaurant={restaurant}
          isOpen={isRestaurantInfoModalOpen}
          onClose={() => setIsRestaurantInfoModalOpen(false)}
        />
      )}

      <BannerDetailModal
        banner={selectedBanner}
        onClose={() => setSelectedBanner(null)}
      />
    </Layout>
  );
};

export default MenuPage;
